// Third party
import React from 'react';

// Types
import Comparison from '@/types/comparison';
import ThemeMode from '@/types/theme-mode';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';

// Components
import { ComparisonTopBarContactButton, ComparisonTopBarName, ComparisonTopBarNavigation } from '../../comparison/ComparisonTopBarNavigation';
import { CloseButton, Button } from '../../../components/buttons';
import ComparisonSaveButton from '../../comparison/ComparisonSaveButton';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { comparisonIsReadOnly, comparisonIsCompliance, getComparisonContactName } from '@/utils/comparison';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

type DefaultHeaderProps = {
  comparison?: Comparison;
  isSaving: boolean;
  theme?: string;
  isEditVisible: boolean;
  isEditPinned: boolean;
  user?: User;
  canPortfolioSave: boolean;
  setComparisonName: (name: string) => void;
  refreshTemplate: () => void;
  redirectToContactPage: () => void;
  handleProposalClose: () => void;
  goBackToComparison: () => void;
  onSaveComparison: (isNewlyAssigned?: boolean) => Promise<Comparison>;
  setComparisonContact: (contact: Nullable<GenericContact>) => void;
  changeItemId: (oldId: string, newId: string) => void;
  onSavePortfolio: (portfolio: Portfolio, contact?: GenericContact) => Promise<Portfolio>;
};

const DefaultHeader = ({
  comparison,
  isSaving,
  theme,
  setComparisonName,
  refreshTemplate,
  redirectToContactPage,
  handleProposalClose,
  goBackToComparison,
  isEditVisible,
  isEditPinned,
  user,
  canPortfolioSave,
  setComparisonContact,
  changeItemId,
  onSavePortfolio,
  onSaveComparison,
}: DefaultHeaderProps) => {
  if (!comparison) return null;

  const isReadOnly = comparisonIsReadOnly(comparison);
  const isCompliance = comparisonIsCompliance(comparison);
  const showSave = user && !isCompliance && !isReadOnly && userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_SAVE);
  const canSave = showSave && !isEditVisible && !isEditPinned && !canPortfolioSave;

  const getFarRightItems = () => {
    const componentsArray: React.ReactNode[] = [];

    if (comparison.contact !== undefined) {
      componentsArray.push(
        <ComparisonTopBarContactButton
          contact={comparison.contact}
          contactName={getComparisonContactName(comparison)}
          onClick={redirectToContactPage}
          isLightVersion={theme === ThemeMode.LIGHT}
        />,
      );
    }

    componentsArray.push(<CloseButton key="ProposalTopBarNavigation.CloseButton" onClick={handleProposalClose} className="top-bar-button" withText dark />);

    return componentsArray;
  };

  const getTopBarLeftItems = () => {
    const componentsArray: React.ReactNode[] = [
      <Button
        key="ProposalTopBarNavigation.BackButton"
        className="top-bar-button"
        onClick={goBackToComparison}
        aria-label={formatMessage('Proposal.ReturnToComparison')}
        icon="arrow-thin-left"
      >
        <FormattedMessage id="Common.Back" />
      </Button>,
    ];

    if (showSave && user) {
      componentsArray.push(
        <ComparisonSaveButton
          key="ProposalTopBarNavigation.ComparisonSaveButton"
          user={user}
          comparison={comparison}
          disabled={!canSave}
          onSaveComparison={onSaveComparison}
          onSetComparisonName={setComparisonName}
          onSetComparisonContact={setComparisonContact}
          onChangeItemId={changeItemId}
          onSavePortfolio={onSavePortfolio}
        />,
      );
    }

    if (import.meta.env.DEV) {
      componentsArray.push(
        <Button
          key="ProposalTopBarNavigation.Reload"
          className="top-bar-button"
          onClick={refreshTemplate}
          aria-label={formatMessage('Proposal.Reload')}
          icon="spin-arrow"
        >
          <FormattedMessage id="Proposal.Reload" />
        </Button>,
      );
    }

    return componentsArray;
  };

  return (
    <ComparisonTopBarNavigation
      className="is-proposal"
      leftItems={getTopBarLeftItems()}
      middleItems={[
        <ComparisonTopBarName key={comparison._id} setComparisonName={setComparisonName} comparison={comparison} readOnly={isReadOnly} isSaving={isSaving} />,
      ]}
      farRightItems={getFarRightItems()}
    />
  );
};

export default DefaultHeader;
